import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import {
	DataPerformanceSummaryRequestBodyObjectType,
	type DataPerformanceSummaryType,
} from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';

import DataPerformanceSummaryBase from 'webapp/models-custom/data-performance-summary/base';
import type { DataPerformanceSummaryObject as DataPerformanceSummaryObjectBase } from 'webapp/models-custom/data-performance-summary/base';

export interface DataPerformanceSummaryObject extends DataPerformanceSummaryObjectBase {
	objectType: DataPerformanceSummaryType.BOARD | DataPerformanceSummaryType.BOARD_PREVIEW;
}

export default class DataPerformanceSummaryBoard extends DataPerformanceSummaryBase {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;

	objectType;

	constructor(model: DataPerformanceSummaryObject) {
		super(model);

		this.objectType = model.objectType;
	}

	@cached
	get object() {
		if (this.isTypeBoardPreview) {
			return this.mmBoardPreviewsService.findModelByHash(this.objectId || '');
		}

		return (
			this.mmBoardsService.normalBoardModels.find((item) => item.id === this.objectId) || null
		);
	}

	@cached
	get dateRangeFormula() {
		return this.object?.commonBlockDateRange?.formula;
	}

	get title() {
		return this.object?.name || '';
	}

	@cached
	get sources() {
		if (this.object) {
			const sourceIds = (this.object.sources || []).map((o) => o.spaceAccessId);

			return this.mmSourcesService.sourceModels.filter((source) => {
				return source.numericId !== null && sourceIds.includes(source.numericId);
			});
		}

		return [];
	}

	get trend() {
		const id = this.object?.id,
			trend = id ? this.mmDataPerformanceSummariesService.boardsTrendCache[id] : null;

		return trend || this.defaultTrend;
	}

	get isDataIncomplete() {
		return false;
	}

	get isDataEmpty() {
		return false;
	}

	get trackingObjectType() {
		return 'databoard';
	}

	get trackingMetricsNames() {
		const object = this.object;

		if (object) {
			const metricNames: string[] = [];
			object.allBlockObjs.forEach((block) => {
				const blockMetricNames =
					this.mmDataPerformanceSummariesService.getTrackingMetricNamesFromBlock(block);
				metricNames.push(...blockMetricNames);
			});
			return uniq(metricNames);
		}

		return [];
	}
	get summariesRequestBodyBase(): APIData.GenerativeAI.DataPerformance.SummariesRequest | null {
		const typeObjectId = this.isTypeBoardPreview
				? this.object?.boardPreviewId
				: this.object?.numericId,
			typeObject = this.isTypeBoardPreview
				? DataPerformanceSummaryRequestBodyObjectType.BOARD_PREVIEW
				: DataPerformanceSummaryRequestBodyObjectType.BOARD;

		if (typeObjectId) {
			return {
				typeObject,
				typeObjectId,
			};
		}

		return null;
	}

	get metricSetupData() {
		return null; // just for the index template
	}

	protected override getSummaries(refresh = false) {
		super.getSummaries(refresh);

		this.summariesXHR?.done(() => {
			// .then does not mean on success, .done does. Read more before @tracked summariesXHR in app\models-custom\data-performance-summary\base.ts
			next(() => {
				const trend = this.sanitizedSummary?.trend,
					id = this.object?.id;

				if (id && trend && trend !== this.defaultTrend) {
					const cache = this.mmDataPerformanceSummariesService.boardsTrendCache;
					cache[id] = trend;
					this.mmDataPerformanceSummariesService.boardsTrendCache = cache;
				}
			});
		});
	}
}
